import React from 'react';
import './Icon.css';

export type IconName =
  | 'mic'
  | 'mic-stop'
  | 'close'
  | 'chat'
  | 'add'
  | 'bar-chart'
  | 'app-logo'
  | 'minimize'
  | 'keyboard'
  | 'attach'
  | 'edit'
  | 'delete'
  | 'view'
  | 'check'
  | 'check-circle'
  | 'warning'
  | 'info'
  | 'analytics'
  | 'camera'
  | 'search'
  | 'target'
  | 'record'
  | 'build'
  | 'x-circle';

interface IconProps {
  name: IconName;
  size?: number | string;
  className?: string;
  title?: string;
}

const strokeProps = {
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function renderIcon(name: IconName): React.ReactNode {
  switch (name) {
    case 'mic':
      return (
        <path
          {...strokeProps}
          d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
        />
      );
    case 'mic-stop':
      return (
        <>
          <rect x="6" y="6" width="4" height="12" {...strokeProps} />
          <rect x="14" y="6" width="4" height="12" {...strokeProps} />
        </>
      );
    case 'close':
      return <path {...strokeProps} d="M6 18L18 6M6 6l12 12" />;
    case 'chat':
      return (
        <path
          {...strokeProps}
          d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
        />
      );
    case 'add':
      return <path {...strokeProps} d="M12 5v14M5 12h14" />;
    case 'bar-chart':
    case 'analytics':
      return (
        <path
          {...strokeProps}
          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
        />
      );
    case 'app-logo':
      return (
        <>
          <rect x="3" y="3" width="18" height="18" rx="2" fill="rgba(255,255,255,0.92)" stroke="none" />
          <rect x="6" y="14" width="3" height="5" rx="0.5" fill="#22c55e" stroke="none" />
          <rect x="10.5" y="10" width="3" height="9" rx="0.5" fill="#ef4444" stroke="none" />
          <rect x="15" y="7" width="3" height="12" rx="0.5" fill="#3b82f6" stroke="none" />
        </>
      );
    case 'minimize':
      return <path {...strokeProps} d="M5 12h14" />;
    case 'keyboard':
      return (
        <>
          <rect x="2" y="6" width="20" height="12" rx="2" {...strokeProps} />
          <path {...strokeProps} d="M6 10h.01M10 10h.01M14 10h.01M18 10h.01M8 14h8" />
        </>
      );
    case 'attach':
      return (
        <path
          {...strokeProps}
          d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
        />
      );
    case 'edit':
      return (
        <path
          {...strokeProps}
          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
        />
      );
    case 'delete':
      return (
        <path
          {...strokeProps}
          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
        />
      );
    case 'view':
      return (
        <>
          <path {...strokeProps} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path
            {...strokeProps}
            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
          />
        </>
      );
    case 'check':
      return <path {...strokeProps} d="M5 13l4 4L19 7" />;
    case 'check-circle':
      return (
        <>
          <path {...strokeProps} d="M9 12l2 2 4-4" />
          <circle cx="12" cy="12" r="9" {...strokeProps} />
        </>
      );
    case 'warning':
      return (
        <>
          <path
            {...strokeProps}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </>
      );
    case 'info':
      return (
        <>
          <circle cx="12" cy="12" r="9" {...strokeProps} />
          <path {...strokeProps} d="M12 16v-4M12 8h.01" />
        </>
      );
    case 'camera':
      return (
        <>
          <path
            {...strokeProps}
            d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
          />
          <circle cx="12" cy="13" r="3" {...strokeProps} />
        </>
      );
    case 'search':
      return (
        <>
          <circle cx="11" cy="11" r="7" {...strokeProps} />
          <path {...strokeProps} d="M21 21l-4.35-4.35" />
        </>
      );
    case 'target':
      return (
        <>
          <circle cx="12" cy="12" r="9" {...strokeProps} />
          <circle cx="12" cy="12" r="5" {...strokeProps} />
          <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
        </>
      );
    case 'record':
      return <circle cx="12" cy="12" r="6" fill="currentColor" stroke="none" />;
    case 'build':
      return (
        <>
          <path
            {...strokeProps}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <circle cx="12" cy="12" r="3" {...strokeProps} />
        </>
      );
    case 'x-circle':
      return (
        <>
          <circle cx="12" cy="12" r="9" {...strokeProps} />
          <path {...strokeProps} d="M15 9l-6 6M9 9l6 6" />
        </>
      );
    default:
      return null;
  }
}

const Icon: React.FC<IconProps> = ({ name, size = 20, className = '', title }) => {
  const filled = name === 'app-logo' || name === 'record';

  return (
    <svg
      className={`ui-icon ui-icon--${name} ${className}`.trim()}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? undefined : 'none'}
      stroke={filled ? undefined : 'currentColor'}
      strokeWidth={filled ? undefined : 2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
    >
      {title && <title>{title}</title>}
      {renderIcon(name)}
    </svg>
  );
};

export default Icon;
